import FW from '../fw/fw';
import Logger from '../fw/logger';
import GoogleCastText from './google-cast-text';
import GoogleCastHelpers from './google-cast-helpers';


export default class GoogleCastCordova {

  #rmp;
  #castDisplayMessageElement = null;
  #remoteCurrentTime = 0;
  #remoteVolume = 0;
  #castSessionStopping = false;
  #castMediaStatusInterval = null;
  #castMediaEnded = false;
  #castMediaStatusUpdatedFn = null;
  #initCastFn = null;

  constructor(rmp) {
    this.#rmp = rmp;
    this.#rmp.rmpGoogleCastText = new GoogleCastText(rmp);
    this.#rmp.rmpGoogleCastHelpers = new GoogleCastHelpers(rmp);
  }

  get castMediaEnded() {
    return this.#castMediaEnded;
  }

  #resetCast() {
    if (this.#rmp.castMedia) {
      this.#rmp.castMedia.removeUpdateListener(this.#castMediaStatusUpdatedFn);
    }
    this.#castMediaStatusUpdatedFn = null;
    this.#castSessionStopping = false;
    this.#castMediaEnded = false;
    this.#rmp.googleCastConnected = false;
    this.#rmp.castMedia = null;
    this.#rmp.castSession = null;
    window.clearInterval(this.#castMediaStatusInterval);
  }

  #castUpdateDisplayMessage(message) {
    let validatedMessage;
    if (!message) {
      validatedMessage = 'STANDBY';
    } else {
      validatedMessage = message;
    }
    if (!this.#castDisplayMessageElement) {
      this.#castDisplayMessageElement = FW.createElement('div', 'rmp-cast-diplay-message-title');
      this.#rmp.dom.container.appendChild(this.#castDisplayMessageElement);
    }
    Logger.print('google-cast-cordova', `update status message to ${validatedMessage}`);
    FW.setText(this.#castDisplayMessageElement, `${this.#rmp.rmpGoogleCastHelpers.castTitle} ${validatedMessage}`);
  }

  #setupLocalPlayer() {
    Logger.print('google-cast-cordova', 'setup local player');
    this.#castUpdateDisplayMessage('DISCONNECTING');
    this.#rmp.dom.container.classList.remove('rmp-casting', 'rmp-casting-cordova');
    const castIcon = this.#rmp.dom.cast.querySelector('span.rmp-i');
    if (castIcon) {
      castIcon.classList.replace('rmp-i-google-cast-connected', 'rmp-i-google-cast');
    }
    // end session now
    this.destroy(true);
  }

  #addCastingCordovaUI() {
    Logger.print('google-cast-cordova', 'add casting Cordova UI');
    this.#rmp.rmpUIManagement.hideLoadingSpin();
    this.#rmp.dom.container.classList.add('rmp-casting', 'rmp-casting-cordova');
    const castIcon = this.#rmp.dom.cast.querySelector('span.rmp-i');
    if (castIcon) {
      castIcon.classList.replace('rmp-i-google-cast', 'rmp-i-google-cast-connected');
    }
  }

  #castInteraction(event) {
    // This will open a native dialog that will let 
    // the user choose a chromecast to connect to
    // (Or will let you disconnect if you are already connected)
    FW.eventStopPrevent(event);
    if (!this.#rmp.rmpPlaybackManagement.playerInitialized && !this.#rmp.googleCastInit) {
      this.#rmp.googleCastInit = true;
      this.#rmp.emptyInitAdTagUrl = true;
      this.#rmp.initiatePlayback();
    }
    this.#rmp.rmpUIManagement.showChrome();
    if (this.#castSessionStopping) {
      return;
    }
    if (!this.#rmp.googleCastConnected) {
      window.chrome.cast.requestSession(session => {
        // Got a session!
        this.#rmp.castSession = session;
        this.loadCastMedia();
      }, error => {
        this.#resetCast();
        this.#rmp.rmpUtils.warning(16002, error);
      });
    } else {
      this.#setupLocalPlayer();
    }
  }

  #handleMediaEnded() {
    this.#rmp.castSession.addMessageListener('urn:x-cast:com.radiantmediaplayer.cast', (namespace, message) => {
      Logger.print('google-cast-cordova', 'custom message received under ' + namespace);
      const messageObject = JSON.parse(message);
      if (messageObject.type === 'STATUS' && messageObject.message === 'MEDIA_FINISHED') {
        Logger.print('google-cast-cordova', 'MEDIA_FINISHED');
        this.#castUpdateDisplayMessage('ENDED');
        this.#rmp.rmpEventsManagement.pauseCallback();
        this.#castMediaEnded = true;
        if (this.#rmp.hasRelated || this.#rmp.hasPlaylist) {
          this.#rmp.rmpEventsManagement.endedListCallback();
        }
        this.#rmp.dispatch('ended');
      }
    });
  }

  #castMediaStatusUpdated(isAlive) {
    if (!isAlive) {
      return;
    }
    Logger.print('google-cast-cordova', 'udpate to media status');
    const newRemoteCurrentTime = this.#rmp.currentTime;
    if (newRemoteCurrentTime !== this.#remoteCurrentTime) {
      this.#remoteCurrentTime = newRemoteCurrentTime;
      this.#rmp.rmpEventsManagement.timeupdateCallback();
    }
    const newRemoteVolume = this.#rmp.volume;
    if (newRemoteVolume !== this.#remoteVolume) {
      this.#remoteVolume = newRemoteVolume;
      this.#rmp.onVolumeChangeFn();
    }
  }

  #initCast() {
    Logger.print('google-cast-cordova', 'cast_sender.js loaded');
    this.#rmp.off('adcontentresumerequested', this.#initCastFn);
    if (this.#rmp.playerReady) {
      this.init();
    } else {
      this.#rmp.one('ready', this.init.bind(this));
    }
  }

  loadDebug() {
    Logger.print('google-cast-cordova', 'requesting debug mode');
    this.#initCastFn = this.#initCast.bind(this);
    if (!window.chrome.cast || !window.chrome.cast.isAvailable) {
      let castSenderScript = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
      window.__onGCastApiAvailable = (isAvailable) => {
        if (isAvailable && window.chrome.cast) {
          Logger.print('google-cast-cordova', 'API available - at ' + (FW.getNow() - this.#rmp.refTime) + ' ms');
          // in case an ad is on stage when cast framework is loaded we need to defer cast init 
          // when ad has finished to avoid cast icon to appear on linear ad
          if (this.#rmp.linearAdOnStage) {
            this.#rmp.on('adcontentresumerequested', this.#initCastFn);
          } else {
            this.#initCastFn();
          }
          delete window.__onGCastApiAvailable;
        }
      };
      Logger.print('google-cast-cordova', 'cast_sender.js starts loading at ' + (FW.getNow() - this.#rmp.refTime) + ' ms');
      FW.getScript(
        castSenderScript,
        () => { return; },
        this.#rmp.rmpUtils.warning.bind(this.#rmp.rmpUtils, 16000),
        this.#rmp.shadowRoot
      );
    } else {
      this.#initCastFn();
    }
  }

  // eslint-disable-next-line
  setCastAudioTrack() { return; }

  isCastMediaLoaded() {
    if (this.#rmp.googleCastConnected) {
      return true;
    }
    return false;
  }

  loadCastMedia(newSession) {
    Logger.print('google-cast-cordova', 'load media on cast device requested');
    this.#rmp.rmpUIManagement.showLoadingSpin();
    this.#castUpdateDisplayMessage('LOADING');
    let localPlayerCurrentTime = this.#rmp.currentTime;

    const mediaInfo = this.#rmp.rmpGoogleCastHelpers.prepareLoadCastMedia();
    Logger.print('google-cast-cordova', 'mediaInfo:', mediaInfo);
    let request = new window.chrome.cast.media.LoadRequest(mediaInfo);
    this.#rmp.castSession.loadMedia((request), media => {
      Logger.print('google-cast-cordova', 'successfully loaded media on receiving device');
      this.#rmp.pauseRequestedByLoadCastMedia = false;
      this.#rmp.googleCastConnected = true;
      this.#rmp.rmpEventsManagement.playCallback();
      this.#castUpdateDisplayMessage('PLAYING');
      this.#rmp.castMedia = media;
      this.#castMediaStatusUpdatedFn = this.#castMediaStatusUpdated.bind(this);
      this.#rmp.castMedia.addUpdateListener(this.#castMediaStatusUpdatedFn);
      // addUpdateListener does not fire as expected for timeupdate to we poll instead
      window.clearInterval(this.#castMediaStatusInterval);
      this.#castMediaStatusInterval = window.setInterval(() => {
        this.#rmp.castMedia.getStatus();
      }, 1000);
      this.#addCastingCordovaUI();
      this.#handleMediaEnded();
      if (newSession) {
        localPlayerCurrentTime = 0;
      }
      if (localPlayerCurrentTime > 0) {
        this.#rmp.seekTo(localPlayerCurrentTime).catch(error => {
          this.#rmp.rmpUtils.warning(1016, error);
        });
      }
      this.#rmp.dispatch('castmedialoaded');
    }, error => {
      this.#setupLocalPlayer();
      this.#rmp.rmpUIManagement.hideLoadingSpin();
      this.#rmp.dispatch('castmediaerror');
      this.#rmp.rmpUtils.warning(16001, error);
    });
  }

  destroy(resumeLocalPlayer) {
    Logger.print('google-cast-cordova', 'destroy requested');
    if (this.#rmp.castSession) {
      this.#castSessionStopping = true;
      this.#rmp.castSession.stop(
        () => {
          this.#resetCast();
          if (resumeLocalPlayer) {
            this.#rmp.play().catch(error => {
              this.#rmp.rmpUtils.warning(1014, error);
            });
            this.#rmp.seekTo(this.#remoteCurrentTime).catch(error => {
              this.#rmp.rmpUtils.warning(1016, error);
            });
          }
        },
        () => {
          this.#resetCast();
          if (resumeLocalPlayer) {
            this.#rmp.play().catch(error => {
              this.#rmp.rmpUtils.warning(1014, error);
            });
            this.#rmp.seekTo(this.#remoteCurrentTime).catch(error => {
              this.#rmp.rmpUtils.warning(1016, error);
            });
          }
        }
      );
    } else {
      this.#resetCast();
    }
  }

  #sessionListener(session) {
    // The session listener is only called under the following conditions:
    // * will be called shortly window.chrome.cast.initialize is run
    // * if the device is already connected to a cast session
    // Basically, this is what allows you to re-use the same cast session 
    // across different pages and after app restarts
    this.#rmp.castSession = session;
  }

  #receiverListener(receiverAvailable) {
    if (receiverAvailable) {
      if (typeof this.#rmp.dom.cast === 'undefined') {
        Logger.print('google-cast-cordova', 'receiver available - add cast module to UI');
        this.#rmp.rmpModuleManagement.appendToModuleBar('cast');
        const castInteractionFn = this.#castInteraction.bind(this);
        FW.addEvents(['touchend', 'click'], this.#rmp.dom.cast, castInteractionFn);
        this.#rmp.rmpGoogleCastHelpers.filterContentType();
      }
    }
  }

  init() {
    if (typeof window.chrome !== 'undefined' && typeof window.chrome.cast !== 'undefined' &&
      typeof window.chrome.cast.ApiConfig === 'function' && typeof window.chrome.cast.initialize === 'function') {
      Logger.print('google-cast-cordova', 'initialize Google Cast Cordova');
      // use default app id
      let googleCastReceiverAppId = this.#rmp.googleCastReceiverAppId;
      if (googleCastReceiverAppId === 'cast-default') {
        googleCastReceiverAppId = window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID;
      }
      const apiConfig = new window.chrome.cast.ApiConfig(
        new window.chrome.cast.SessionRequest(
          googleCastReceiverAppId,
          [],
          null,
          this.#rmp.googleCastAndroidReceiverCompatible,
          null
        ),
        this.#sessionListener.bind(this),
        this.#receiverListener.bind(this)
      );

      // initialize chromecast, this must be done before using other chromecast features
      window.chrome.cast.initialize(
        apiConfig, () => {
          Logger.print('google-cast-cordova', 'Google Cast Cordova initialized successfully');
        }, error => {
          this.#rmp.rmpUtils.warning(16003, error);
        }
      );
      this.#rmp.dispatch('castapiavailable');
    }
  }
}
